import React from 'react';
import styled from 'styled-components';
import { useContextSelector } from 'use-context-selector';

import HeadTitle from '../../components/HeadTitle';
import { GenericModal } from './components/GenericModal';
import ContainerPage from '../../components/ContainerPage';
import LoadingOverlay from '../../components/LoadingOverlay';
import SystemUnavailable from '../../components/SystemUnavailable';
import ModalNoEligibility from '../../components/ModalNoEligibility';
import { ModalProductNotCovered } from '../../components/ModalProductNotCovered';

import { usePatientModule } from '../../hooks/usePatientModule';
import { useSelectCoverage } from '../../hooks/useSelectCoverage';
import { handleGenericModals } from '../../utils/genericModals';

import { ActionButton } from './components/ActionButton';
import { ProductList } from './components/ProductList';

import { CartContext } from '../../contexts/CartContext';
import { RouterContext } from '../../contexts/RouterContext';
import { ModalsContext } from '../../contexts/ModalsContext';
import { ModuleContext } from '../../contexts/ModuleContext';
import { PatientContext } from '../../contexts/PatientContext';
import { PaymentContext } from '../../contexts/PaymentContext';
import { ServiceContext } from '../../contexts/ServiceContext';
import { ScheduleContext } from '../../contexts/ScheduleContext';

import { INFO_GENERIC_RULES_CONDITION, INFO_GENERIC_RULES_WHEN } from '../../utils/constants';

import { useProductSelector } from './index.model';
import { ProductListCheck } from './components/ProductListCheck';
import { gtmEvent } from '../../utils/gtm';

const Container = styled.div`
  padding-bottom: 20px;
`;

export const ProductSelector: React.FC = () => {
  const { goTo } = useContextSelector(RouterContext, state => state);
  const { openModal } = useContextSelector(ModalsContext, state => state);
  const { changeProduct, addProductsToCart } = useContextSelector(CartContext, state => state);
  const { selectedService } = useContextSelector(ServiceContext, state => state);
  const { setEligibilityPlans, eligibilityPlans } = useContextSelector(PaymentContext, state => state);
  const { patientSelected, addPatientList } = useContextSelector(PatientContext, state => state);

  const {
    selectedItem,
    changeSchedule,
    scheduleSpecialistSelected,
    setScheduleSpecialistSelected,
    changeScheduleSpecialist,
    addUnitDatesAvailable
  } = useContextSelector(ScheduleContext, state => state);

  const {
    handleSelectProduct: selectProductScheduleModule,
    selectItemToSchedule,
    selectedItemToSchedule,
    logged
  } = useContextSelector(ModuleContext, state => state);

  const {
    genericModal,
    createSchedule,
    isModalNoEligibility,
    setIsModalNoEligibility,
    isModalSystemUnavailableVisible,
    isModalProductNotCoveredVisible,
    setIsModalSystemUnavailableVisible,
    setIsModalProductNotCoveredVisible
  } = usePatientModule({
    goTo,
    openModal,
    changeProduct,
    changeSchedule,
    addPatientList,
    eligibilityPlans,
    setEligibilityPlans,
    changeScheduleSpecialist,
    scheduleSpecialistSelected,
    setScheduleSpecialistSelected,
    selectedSchedule: selectedItem
  });

  const { handleSelectedCoverage } = useSelectCoverage({
    openModal,
    changeSchedule,
    addUnitDatesAvailable,
    scheduleSelected: selectedItem,
    handleModalProps: genericModal.handleModalProps,
    productRules: []
  });

  const {
    loading,
    setLoading,
    productList,
    setProductList,
    selectedProduct,
    productListCheck,
    setProductListCheck,
    checkMandatoryModal,
    handleSelectProduct,
    routeProductSelector,
    handleMultiselectedProducts
  } = useProductSelector({
    addProductsToCart,
    openModal: genericModal.openModal,
    scheduleSelected: selectedItem,
    selectProductScheduleModule,
    selectedItemToSchedule,
    handleSelectedCoverage,
    selectItemToSchedule,
    handleGenericModals,
    patientSelected,
    createSchedule,
    changeSchedule,
    logged,
    goTo
  });

  function handleDisableActionButton() {
    if (selectedProduct?.id) return !selectedProduct?.id;
    return !productListCheck.some(item => item.selected);
  }

  if (!selectedService) return null;

  return (
    <ContainerPage
      childrenFooter={
        <ActionButton
          disable={handleDisableActionButton()}
          handleActionButton={() => {
            if (selectedProduct) {
              const genericModals = selectedProduct.rules ? handleGenericModals(selectedProduct.rules) : undefined;

              if (genericModals) {
                const modal = checkMandatoryModal(selectedProduct, INFO_GENERIC_RULES_WHEN.after_product_selection);

                if (modal)
                  genericModal.openModal(
                    INFO_GENERIC_RULES_WHEN.after_product_selection,
                    INFO_GENERIC_RULES_CONDITION.always
                  );
                else routeProductSelector({ alreadyShowModal: false });
              } else routeProductSelector({ alreadyShowModal: false });
            }

            if (productListCheck.some(item => item.selected)) handleMultiselectedProducts();
          }}
        />
      }
    >
      <Container>
        <HeadTitle title="Escolha o produto" />
        {selectedService.id === process.env.REACT_APP_CHECKUP_PRODUCT_ID ? (
          <ProductListCheck
            products={productListCheck}
            setProducts={setProductListCheck}
            setProductList={setProductList}
          />
        ) : (
          <ProductList
            products={productList}
            setProductList={setProductList}
            selected={selectedProduct}
            handleSelect={handleSelectProduct}
          />
        )}
      </Container>
      <GenericModal
        when={genericModal.genericModalWhen}
        condition={genericModal.genericModalCondition}
        visible={genericModal.modalGenericVisible}
        isVisible={genericModal.setModalGenericVisible}
        productList={productList}
        patientSelected={patientSelected}
        selectedProduct={selectedProduct}
        selectedService={selectedService}
        setModalGenericVisible={genericModal.setModalGenericVisible}
        handleActionButtonModal={genericModal.handleActionButtonModal}
        genericModalCondition={genericModal.genericModalCondition}
        routeProductSelector={routeProductSelector}
        setGenericModalWhen={genericModal.setGenericModalWhen}
        handleModalProps={genericModal.handleModalProps}
        setLoading={setLoading}
        genericModal={genericModal}
      />
      <SystemUnavailable
        visible={isModalSystemUnavailableVisible}
        onClose={() => {
          setIsModalSystemUnavailableVisible(false);
        }}
      />
      <ModalProductNotCovered
        visible={isModalProductNotCoveredVisible}
        onClose={() => setIsModalProductNotCoveredVisible(false)}
      />
      <ModalNoEligibility
        visible={isModalNoEligibility}
        onClose={() => {
          setIsModalNoEligibility(false);
        }}
      />
      <LoadingOverlay loading={loading} />
    </ContainerPage>
  );
};
